export type AdminGroupKey =
  | 'dashboard'
  | 'crm'
  | 'sales'
  | 'customers'
  | 'delivery'
  | 'partnerships'
  | 'pages';

export interface AdminGroupTab {
  key: string;
  label: string;
  href: string;
  match: (pathname: string) => boolean;
}

export interface AdminGroup {
  key: AdminGroupKey;
  label: string;
  href?: string;
  disabled?: boolean;
  disabledReason?: string;
  tabs: AdminGroupTab[];
}

function exact(href: string): (pathname: string) => boolean {
  return (pathname) => pathname === href;
}

function prefix(href: string): (pathname: string) => boolean {
  return (pathname) => pathname === href || pathname.startsWith(`${href}/`);
}

export const adminGroups: AdminGroup[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    href: '/admin',
    tabs: [],
  },
  {
    key: 'crm',
    label: 'CRM',
    disabled: true,
    disabledReason: 'Coming soon',
    tabs: [],
  },
  {
    key: 'sales',
    label: 'Sales',
    href: '/admin/leads',
    tabs: [
      { key: 'leads', label: 'Leads', href: '/admin/leads', match: prefix('/admin/leads') },
      { key: 'pipeline', label: 'Pipeline', href: '/admin/pipeline', match: prefix('/admin/pipeline') },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    href: '/admin/clients',
    tabs: [
      { key: 'clients', label: 'Clients', href: '/admin/clients', match: prefix('/admin/clients') },
      { key: 'investors', label: 'Investors', href: '/admin/investors', match: exact('/admin/investors') },
    ],
  },
  {
    key: 'delivery',
    label: 'Delivery',
    href: '/admin/delivery',
    tabs: [
      { key: 'delivery', label: 'Delivery', href: '/admin/delivery', match: exact('/admin/delivery') },
      {
        key: 'modular',
        label: 'Modular',
        href: '/admin/delivery/modular',
        match: (pathname) =>
          prefix('/admin/delivery/modular')(pathname) ||
          prefix('/admin/syndication-deals')(pathname),
      },
    ],
  },
  {
    key: 'partnerships',
    label: 'Partnerships',
    href: '/admin/partners',
    tabs: [
      { key: 'partners', label: 'Partners', href: '/admin/partners', match: prefix('/admin/partners') },
      { key: 'syndicates', label: 'Syndicates', href: '/admin/syndicates', match: exact('/admin/syndicates') },
    ],
  },
  {
    key: 'pages',
    label: 'Pages',
    href: '/admin/pages',
    tabs: [
      { key: 'publicPages', label: 'Public Pages', href: '/admin/pages', match: exact('/admin/pages') },
      {
        key: 'manual',
        label: 'Manual',
        href: '/admin/application-manual',
        match: prefix('/admin/application-manual'),
      },
      { key: 'campaigns', label: 'Campaigns', href: '/admin/campaigns', match: prefix('/admin/campaigns') },
    ],
  },
];

export function getAdminGroup(key: AdminGroupKey): AdminGroup | undefined {
  return adminGroups.find((group) => group.key === key);
}

export function getGroupForPath(pathname: string): AdminGroup | undefined {
  for (const group of adminGroups) {
    if (group.disabled) continue;
    if (group.tabs.some((tab) => tab.match(pathname))) return group;
    if (group.tabs.length === 0 && group.href && pathname === group.href) return group;
  }
  return undefined;
}
